/* eslint-disable no-eq-null */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';
import { withRouter } from 'react-router-dom';
import ReactRouterPropTypes from 'react-router-prop-types';
import { Toaster } from 'react-hot-toast';
import { Howl } from 'howler';
import styled from 'styled-components';
import MainTitleBar from './MainTitleBar';
import MapPage from './Map/MapPage';
import TimeLinePage from './TimeLine/TimeLinePage';
import TimeLineTilePage from './TimeLine/TimeLineTilePage';
import OperatorPage from './Operator/OperatorPage';
import StaffPage from './Staff/StaffPage';
import CompanyPage from './Company/CompanyPage';
import GroupPage from './Group/GroupPage';
import DevicePage from './Device/DevicePage';
import MasterPage from './Master/MasterPage';
import ActionLogPage from './ActionLog/ActionLogPage';
import ReportPage from './Report/ReportPage';
import ReportEditPage from './ReportEdit/ReportEditPage';
import UserInfo from './UserInfo/UserInfo';
import Loading from './Loading';
import UserPrompt from './UserPrompt';
import IncomingAlertBar from './Remote/IncomingAlertBar';
import { updateUserDeviceInfo } from '../actions/api/apis';
import { FULLSCREEN_MODE, PAGE_PATH } from '../lib/constants';
import TrashPage from './Trash/TrashPage';
import ToolBar from './Parts/ToolBar';
import { isGeneral, isRootOrSp } from '../lib/tools';
import callSound from '../sounds/call.mp3';
import AccessControlPage from './AccessControl/AccessControlPage';

const Main = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  overflow-x: auto;
`;
const Contents = styled.div`
  flex: 1 1 0px;
  width: 100%;
  display: flex;
  flex-direction: row;
  height: calc(100% - 82px);
  overflow-y: ${props => (props.shouldScroll ? 'scroll' : 'hidden')};
  overflow-x: hidden;
  @media (max-height: 530px) and (orientation: landscape),
    (max-height: 1024px) and (orientation: portrait) {
    height: ${props => (props.hideMainTitle ? '100%' : 'calc(100% - 114px)')};
  }
`;
const Block = styled.div`
  flex: 1;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
const Content = styled.div`
  height: 50%;
`;

const soundManager = new Howl({
  src: [callSound],
  loop: true,
});

const MainPage = ({
  searchBoxHeight,
  openUserInfo,
  userInfo,
  license,
  isLoading,
  loadingMessages,
  apiToken,
  history,
  companyConfirmData,
  companyConfirmDataError,
  timelines,
  totalNumber,
  totalPages,
  currentPage,
  searchOptions,
  searchOptionsError,
  timelineTags,
  timelineSelectedTagIds,
  checkbox,
  menuIsOpen,
  dispatch,
  operators,
  companies,
  companyTab,
  currentCompanyInfo,
  openUpdateCompanyDialog,
  openCreateCompanyDialog,
  openLocationSearchMap,
  companySearchOptions,
  fullScreenMode,
  version,
  checkedLicenseOver,
  licenseCapacityStatus,
  devices,
  openUpdateDeviceDialog,
  deviceConfirmData,
  deviceConfirmDataError,
  displayDeviceList,
  displayTimelineList,
  deviceInformations,
  callingRooms,
  isMobile,
  provider,
  allowRemoteSupport,
  calendarData,
  timeLineScreenMode,
  operatorWorks,
  timelineExecuteEvent,
  openUpdateTagsDialog,
  isOpenBlackboardList,
  userGroups,
  targetCompanyInfo,
  targetGroupId,
  innerRole,
  isCompanyAdmin,
  groups,
  incomingAlertTarget,
  callingRoomsMembers,
}) => {
  const pagenation = { totalNumber, totalPages, currentPage };
  const showIncomingAlertBar =
    !isMobile &&
    allowRemoteSupport &&
    incomingAlertTarget &&
    incomingAlertTarget.appRoomId;
  if (showIncomingAlertBar && !soundManager.playing()) {
    soundManager.play();
  } else if (!showIncomingAlertBar && soundManager.playing()) {
    soundManager.stop();
  }
  const onOpenCalendar = name => dispatch({ type: 'OPEN_CALENDAR', name });
  const onCloseCalendar = () => dispatch({ type: 'CLOSE_CALENDAR' });
  const isTimeline = fullScreenMode === FULLSCREEN_MODE.timeline;
  const isTrash = fullScreenMode === FULLSCREEN_MODE.trash;
  const hideMainTitle = isTimeline;
  const redirect = path => history.push(path);
  const openDetail = (id, params) => {
    redirect(`${provider.pathName}/ds/media/${id}`);
    dispatch({ type: 'OPEN_MEDIA_DETAIL', params });
  };
  const currentOperator =
    (operators && operators.length > 0 && operators[0]) || {}; // 作業者管理画面用
  const targetGroup = userGroups.find(group => group.id === targetGroupId);
  const targetGroupName = (targetGroup && targetGroup.name) || '';
  const useGroupLatLng =
    (targetGroupId &&
      targetGroup &&
      targetGroup.lat != null &&
      targetGroup.lng != null) ||
    false;
  const mapCenterSite =
    (useGroupLatLng && { lat: targetGroup.lat, lng: targetGroup.lng }) ||
    userInfo.mapCenterSite;
  // operatorsは作業者一覧と地図とで同一のものを使用するため、一般作業者向け地図用はここでfilterする
  const operatorsForMap = isGeneral(innerRole)
    ? operators.filter(({ operatorId }) => operatorId === userInfo.operatorId)
    : operators;

  // 画面のスクロールが必要かどうか。styled-componentsのpropsを使用する
  const shouldScroll =
    fullScreenMode === FULLSCREEN_MODE.master ||
    fullScreenMode === FULLSCREEN_MODE.timelineTile;

  return (
    <Main className="mainPage">
      {openUserInfo && (
        <UserInfo
          userInfo={userInfo}
          openUserInfo={openUserInfo}
          onClose={() => dispatch({ type: 'CLOSE_USER_INFO_DIALOG' })}
        />
      )}
      <UserPrompt />
      <Toaster />
      {showIncomingAlertBar && (
        <IncomingAlertBar
          apiToken={apiToken}
          dispatch={dispatch}
          incomingAlertTarget={incomingAlertTarget}
          callingRooms={callingRooms}
        />
      )}
      <MainTitleBar
        apiToken={apiToken}
        allowRemoteSupport={allowRemoteSupport}
        isMobile={isMobile}
        toggleUserInfo={openUserInfo}
        menuIsOpen={menuIsOpen}
        userInfo={userInfo}
        license={license}
        version={version}
        dispatch={dispatch}
        hidden={hideMainTitle}
        provider={provider}
        innerRole={innerRole}
        isCompanyAdmin={isCompanyAdmin}
        callingRooms={callingRooms}
        callingRoomsMembers={callingRoomsMembers}
      />
      <ToolBar
        hidden={isTrash || isRootOrSp(userInfo.role)}
        companyName={targetCompanyInfo.companyName || ''}
        groupName={targetGroupName}
        onSelect={groupId => dispatch({ type: 'UPDATE_TARGET_GROUP', groupId })}
      />
      <Contents hideMainTitle={hideMainTitle} shouldScroll={shouldScroll}>
        {fullScreenMode === FULLSCREEN_MODE.map && (
          <Block>
            <MapPage
              redirect={redirect}
              dispatch={dispatch}
              operators={operatorsForMap}
              mapCenterSite={mapCenterSite}
              fullscreen={fullScreenMode === FULLSCREEN_MODE.map}
            />
          </Block>
        )}
        {isTimeline && (
          <Block>
            <TimeLinePage
              redirect={redirect}
              searchBoxHeight={searchBoxHeight}
              apiToken={apiToken}
              innerRole={innerRole}
              fullscreen={isTimeline}
              timelines={timelines}
              pagenation={pagenation}
              searchOptions={searchOptions}
              searchOptionsError={searchOptionsError}
              tags={timelineTags}
              selectedTagIds={timelineSelectedTagIds}
              checkbox={checkbox}
              companyLicenseInfo={userInfo.companyLicenseInfo || {}}
              currentCompanyInfo={currentCompanyInfo}
              calendarData={calendarData}
              onOpenCalendar={onOpenCalendar}
              onCloseCalendar={onCloseCalendar}
              dispatch={dispatch}
              openDetail={openDetail}
              timeLineScreenMode={timeLineScreenMode}
              provider={provider}
              targetGroupId={targetGroupId}
              groups={groups}
              userInfo={userInfo}
              userGroups={userGroups}
            />
          </Block>
        )}
        {isTrash && (
          <Block>
            <TrashPage
              searchBoxHeight={searchBoxHeight}
              apiToken={apiToken}
              dispatch={dispatch}
              innerRole={innerRole}
              redirect={redirect}
            />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.dashboard && (
          <Fragment key="MainPageDashboard">
            <MediaQuery query="(min-width: 927px)">
              <Block>
                <Content>
                  <TimeLinePage
                    searchBoxHeight={searchBoxHeight}
                    apiToken={apiToken}
                    innerRole={innerRole}
                    fullscreen={false}
                    timelines={timelines}
                    pagenation={pagenation}
                    searchOptions={searchOptions}
                    searchOptionsError={searchOptionsError}
                    tags={timelineTags}
                    selectedTagIds={timelineSelectedTagIds}
                    checkbox={checkbox}
                    companyLicenseInfo={userInfo.companyLicenseInfo || {}}
                    currentCompanyInfo={currentCompanyInfo}
                    calendarData={calendarData}
                    onOpenCalendar={onOpenCalendar}
                    onCloseCalendar={onCloseCalendar}
                    dispatch={dispatch}
                    openDetail={openDetail}
                    timeLineScreenMode={timeLineScreenMode}
                    provider={provider}
                    targetGroupId={targetGroupId}
                    groups={groups}
                    userInfo={userInfo}
                    userGroups={userGroups}
                  />
                </Content>
                <Content>
                  <MapPage
                    redirect={redirect}
                    dispatch={dispatch}
                    operators={operatorsForMap}
                    mapCenterSite={mapCenterSite}
                    fullscreen={false}
                  />
                </Content>
              </Block>
            </MediaQuery>
            <Block>
              <OperatorPage redirect={redirect} />
            </Block>
          </Fragment>
        )}
        {fullScreenMode === FULLSCREEN_MODE.timelineTile && (
          <Block>
            <TimeLineTilePage
              dispatch={dispatch}
              apiToken={apiToken}
              redirect={redirect}
              searchBoxHeight={searchBoxHeight}
              userInfo={userInfo}
              innerRole={innerRole}
              license={license}
              isMobile={isMobile}
              operatorWorks={operatorWorks}
              currentOperator={currentOperator}
              displayDeviceList={displayDeviceList}
              displayTimelineList={displayTimelineList}
              userDevices={deviceInformations}
              allowRemoteSupport={allowRemoteSupport}
              onSendLocation={id =>
                dispatch(
                  updateUserDeviceInfo(apiToken, {
                    operatorId: operators[0].operatorId,
                    deviceId: id,
                    updateType: 1,
                  }),
                )
              }
              onDeleteDevice={id =>
                dispatch(
                  updateUserDeviceInfo(apiToken, {
                    operatorId: operators[0].operatorId,
                    deviceId: id,
                    updateType: 2,
                  }),
                )
              }
              calendarData={calendarData}
              onOpenCalendar={onOpenCalendar}
              onCloseCalendar={onCloseCalendar}
              openDetail={openDetail}
              timeLineScreenMode={timeLineScreenMode}
              executeEvent={timelineExecuteEvent}
              openUpdateTagsDialog={openUpdateTagsDialog}
              isOpenBlackboardList={isOpenBlackboardList}
              openBlackboardList={deviceId =>
                dispatch({ type: 'OPEN_BLACKBOARD_LIST_DIALOG', deviceId })
              }
              supportDevice={(deviceId, blackboardId) => {
                const { operatorId } = currentOperator;
                const link = `${PAGE_PATH}?operatorIds=[${operatorId}]&deviceId=${deviceId}&blackboardId=${blackboardId}`;
                window.open(link);
              }}
              targetGroupId={targetGroupId}
              userGroups={userGroups}
            />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.staff && (
          <Block>
            <StaffPage searchBoxHeight={searchBoxHeight} />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.company && (
          <Block>
            <CompanyPage
              role={userInfo.role}
              searchBoxHeight={searchBoxHeight}
              companyTab={companyTab}
              apiToken={apiToken}
              currentCompanyInfo={currentCompanyInfo}
              confirmData={companyConfirmData}
              confirmDataError={companyConfirmDataError}
              companies={companies}
              openUpdateCompanyDialog={openUpdateCompanyDialog}
              openCreateCompanyDialog={openCreateCompanyDialog}
              openLocationSearchMap={openLocationSearchMap}
              searchOptions={companySearchOptions}
              onClose={() => {
                dispatch({ type: 'CLOSE_UPDATE_COMPANY_DIALOG' });
                dispatch({ type: 'CLOSE_CREATE_COMPANY_DIALOG' });
              }}
              checkedLicenseOver={checkedLicenseOver}
              licenseCapacityStatus={licenseCapacityStatus}
              dispatch={dispatch}
            />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.group && (
          <Block>
            <GroupPage />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.device && (
          <Block>
            <DevicePage
              dispatch={dispatch}
              role={userInfo.role}
              apiToken={apiToken}
              devices={devices}
              openUpdateDeviceDialog={openUpdateDeviceDialog}
              confirmData={deviceConfirmData}
              confirmDataError={deviceConfirmDataError}
              onClose={() => {
                dispatch({ type: 'CLOSE_UPDATE_DEVICE_DIALOG' });
              }}
            />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.master && (
          <Block>
            <MasterPage />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.actionLog && (
          <Block>
            <ActionLogPage dispatch={dispatch} apiToken={apiToken} />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.report && (
          <Block>
            <ReportPage dispatch={dispatch} apiToken={apiToken} />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.reportEdit && (
          <Block>
            <ReportEditPage dispatch={dispatch} apiToken={apiToken} />
          </Block>
        )}
        {fullScreenMode === FULLSCREEN_MODE.accessControl && (
          <Block>
            <AccessControlPage />
          </Block>
        )}
      </Contents>
      {isLoading && <Loading loadingMessages={loadingMessages} />}
    </Main>
  );
};

MainPage.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchBoxHeight: PropTypes.number,
  openUserInfo: PropTypes.bool.isRequired,
  userInfo: PropTypes.object.isRequired,
  license: PropTypes.object.isRequired,
  operators: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadingMessages: PropTypes.array.isRequired,
  apiToken: PropTypes.string.isRequired,
  menuIsOpen: PropTypes.bool.isRequired,
  timelines: PropTypes.array.isRequired,
  totalNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  searchOptions: PropTypes.object.isRequired,
  searchOptionsError: PropTypes.object.isRequired,
  timelineTags: PropTypes.object.isRequired,
  timelineSelectedTagIds: PropTypes.array.isRequired,
  checkbox: PropTypes.object.isRequired,
  openUpdateCompanyDialog: PropTypes.bool.isRequired,
  openCreateCompanyDialog: PropTypes.bool.isRequired,
  companyTab: PropTypes.number.isRequired,
  currentCompanyInfo: PropTypes.object.isRequired,
  companyConfirmData: PropTypes.object.isRequired,
  companyConfirmDataError: PropTypes.object.isRequired,
  companySearchOptions: PropTypes.object.isRequired,
  openLocationSearchMap: PropTypes.bool.isRequired,
  fullScreenMode: PropTypes.number.isRequired,
  version: PropTypes.object.isRequired,
  checkedLicenseOver: PropTypes.bool.isRequired,
  licenseCapacityStatus: PropTypes.object.isRequired,
  devices: PropTypes.array.isRequired,
  openUpdateDeviceDialog: PropTypes.bool.isRequired,
  deviceConfirmData: PropTypes.object.isRequired,
  deviceConfirmDataError: PropTypes.object.isRequired,
  displayDeviceList: PropTypes.bool.isRequired,
  displayTimelineList: PropTypes.bool.isRequired,
  deviceInformations: PropTypes.array.isRequired,
  isOpenBlackboardList: PropTypes.bool.isRequired,
  callingRooms: PropTypes.array.isRequired,
  isMobile: PropTypes.bool.isRequired,
  provider: PropTypes.object.isRequired,
  allowRemoteSupport: PropTypes.bool.isRequired,
  calendarData: PropTypes.object.isRequired,
  timeLineScreenMode: PropTypes.number.isRequired,
  operatorWorks: PropTypes.object.isRequired,
  timelineExecuteEvent: PropTypes.string.isRequired,
  openUpdateTagsDialog: PropTypes.bool.isRequired,
  userGroups: PropTypes.array.isRequired,
  targetCompanyInfo: PropTypes.object.isRequired,
  targetGroupId: PropTypes.string.isRequired,
  innerRole: PropTypes.number.isRequired,
  isCompanyAdmin: PropTypes.bool.isRequired,
  groups: PropTypes.array.isRequired,
  incomingAlertTarget: PropTypes.object.isRequired,
  callingRoomsMembers: PropTypes.array.isRequired,
};

MainPage.defaultProps = {
  searchBoxHeight: 0,
};

const mapStateToProps = state => ({
  openUserInfo: state.main.openUserInfo,
  userInfo: state.main.userInfo,
  license: state.main.license,
  operators: state.operator.operators,
  companies: state.company.companies,
  isLoading: state.main.isLoading,
  loadingMessages: state.main.loadingMessages,
  apiToken: state.main.apiToken,
  menuIsOpen: state.main.menuIsOpen,
  timelines: state.timeline.timelines,
  totalNumber: state.timeline.totalNumber,
  totalPages: state.timeline.totalPages,
  currentPage: state.timeline.currentPage,
  searchOptions: state.timeline.searchOptions,
  searchOptionsError: state.timeline.searchOptionsError,
  timelineTags: state.timeline.selectOptions.tags,
  timelineSelectedTagIds: state.timeline.selectedTagIds,
  checkbox: state.timeline.checkbox,
  openUpdateCompanyDialog: state.company.openUpdateCompanyDialog,
  openCreateCompanyDialog: state.company.openCreateCompanyDialog,
  currentCompanyInfo: state.company.currentCompanyInfo,
  companyConfirmData: state.company.confirmData,
  companyTab: state.company.companyTab,
  companyConfirmDataError: state.company.confirmDataError,
  companySearchOptions: state.company.searchOptions,
  openLocationSearchMap: state.position.openLocationSearchMap,
  fullScreenMode: state.main.fullScreenMode,
  version: state.main.version,
  checkedLicenseOver: state.company.checkedLicenseOver,
  licenseCapacityStatus: state.company.licenseCapacityStatus,
  devices: state.device.devices,
  openUpdateDeviceDialog: state.device.openUpdateDeviceDialog,
  deviceConfirmData: state.device.confirmData,
  deviceConfirmDataError: state.device.confirmDataError,
  displayDeviceList: state.main.displayDeviceList,
  displayTimelineList: state.main.displayTimelineList,
  deviceInformations: state.main.deviceInformations,
  isOpenBlackboardList: state.main.isOpenBlackboardList,
  callingRooms: state.room.callingRooms,
  isMobile: state.main.isMobile,
  provider: state.main.provider,
  allowRemoteSupport: state.main.allowRemoteSupport,
  calendarData: state.main.calendarData,
  timeLineScreenMode: state.main.timeLineScreenMode,
  operatorWorks: state.operatorWorks,
  timelineExecuteEvent: state.timeline.executeEvent,
  openUpdateTagsDialog: state.timeline.openUpdateTagsDialog,
  userGroups: state.main.userGroups,
  targetCompanyInfo: state.main.targetCompanyInfo,
  targetGroupId: state.main.targetGroupId,
  innerRole: state.main.innerRole,
  isCompanyAdmin: state.main.isCompanyAdmin,
  groups: state.group.groups,
  groupSearchOptions: state.group.searchOptions,
  incomingAlertTarget: state.room.incomingAlertTarget,
  callingRoomsMembers: state.room.callingRoomsMembers,
});

export default withRouter(connect(mapStateToProps)(MainPage));
